import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../lib/utils'

const beamPath = 'M31 .5h32M0 .5h32m30 31h32m-1 0h32m-1 31h32M62.5 32V0m62 63V31'

const flowOrderSpacing = 0.22

const getPercent = (value) => Number(value.replace('%', ''))

const beams = [
  { left: '8%', top: '6%', scale: 2.1 },
  { left: '36%', top: '3%', scale: 1.95 },
  { left: '64%', top: '9%', scale: 2.25 },
  { left: '83%', top: '2%', scale: 2.05 },
  { left: '28%', top: '72%', scale: 2.2 },
  { left: '84%', top: '76%', scale: 2.15 },
  { left: '20%', top: '36%', scale: 1.9 },
  { left: '55%', top: '45%', scale: 2.3 },
  { left: '72%', top: '62%', scale: 2.0 },
  { left: '45%', top: '84%', scale: 2.35 },
  { left: '10%', top: '78%', scale: 2.25 }
]

const sequencedBeams = [...beams]
  .sort((a, b) => {
    const topDiff = getPercent(b.top) - getPercent(a.top)
    if (topDiff !== 0) return topDiff
    return getPercent(b.left) - getPercent(a.left)
  })
  .map((beam, index) => ({
    ...beam,
    delay: index * flowOrderSpacing
  }))

export const GridBeam = ({ children, className, style }) => (
  <div
    className={cn('relative w-full h-full bg-grid overflow-hidden', className)}
    style={{
      position: 'relative',
      zIndex: 1,
      filter: 'none',
      backdropFilter: 'none',
      ...style
    }}
  >
    {sequencedBeams.map((beam, index) => (
      <Beam
        key={`${beam.left}-${beam.top}`}
        className="absolute pointer-events-none"
        gradientId={`beam-gradient-${index}`}
        left={beam.left}
        top={beam.top}
        scale={beam.scale}
        delay={beam.delay}
        zIndex={20 + index}
      />
    ))}
    {children}
  </div>
)

export const Beam = ({ className, gradientId, left = '0%', top = '0%', scale = 2, delay = 0, zIndex = 20 }) => (
  <svg
    width="156"
    height="63"
    viewBox="0 0 156 63"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{
      left,
      top,
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      zIndex,
        filter: 'brightness(1.45) saturate(1.45)'
    }}
  >
    <motion.path
      d={beamPath}
      stroke={`url(#${gradientId})`}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="6 10"
      animate={{
        strokeDashoffset: [0, -220],
        opacity: [0.2, 1, 0.45, 1, 0.2],
        strokeWidth: [1.9, 2.7, 2.1]
      }}
      initial={{ strokeDashoffset: 0, opacity: 0.15 }}
      transition={{
          duration: 1.5,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'linear',
          repeatDelay: 2.25,
          delay
        }}
      style={{ filter: `url(#${gradientId}-spark)` }}
    />

    <motion.path
      d={beamPath}
      stroke={`url(#${gradientId})`}
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="3 20"
      animate={{
        strokeDashoffset: [120, -220],
        opacity: [0, 0.9, 0.35, 0.95, 0],
        strokeWidth: [1.2, 1.6, 1.2]
      }}
      initial={{ strokeDashoffset: 120, opacity: 0.2 }}
      transition={{
          duration: 1.8,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'linear',
          delay: delay + 0.25,
          repeatDelay: 2.25
        }}
    />
    <defs>
      <motion.linearGradient
        id={gradientId}
        variants={{
          initial: {
            x1: '40%',
            x2: '50%',
            y1: '160%',
            y2: '180%'
          },
          animate: {
            x1: '0%',
            x2: '10%',
            y1: '-40%',
            y2: '-20%'
          }
        }}
        animate="animate"
        initial="initial"
        transition={{
          duration: 2.05,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'linear',
          repeatDelay: 2.25,
          delay
        }}
      >
        <stop stopColor="#09143b" stopOpacity="0" />
        <stop stopColor="#141f5a" stopOpacity="0.96" />
        <stop offset="0.28" stopColor="#1f2d8b" />
        <stop offset="0.56" stopColor="#2f3db0" />
        <stop offset="0.82" stopColor="#5f6ee0" stopOpacity="0.9" />
        <stop offset="1" stopColor="#5f6ee0" stopOpacity="0" />
      </motion.linearGradient>
      <filter id={`${gradientId}-spark`} x="-40%" y="-80%" width="220%" height="260%">
        <feGaussianBlur stdDeviation="2.5" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  </svg>
)
